import { ModalForm, PasswordInput, useForm } from '@faceup/form'
import { useIntl } from '@faceup/localization'
import { trpc, useSuspenseQuery } from '@faceup/trpc'
import { ContentLayout, PageContentSkeleton } from '@faceup/ui'
import { Button, Table, Typography } from '@faceup/ui-base'
import type { AiPromptName } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { Suspense, useState } from 'react'
import { TrpcProvider } from '../../TrpcProvider'
import useRegion, { DEFAULT_SUPPORTED_REGION } from '../../utils/useRegion'
import { AiPromptHistoryModal } from './AiPromptHistoryModal'
import { aiPromptDefinitions } from './aiPromptDefinitions'
import { EditAiPromptModal } from './EditAiPromptModal'

const { Text } = Typography

// Not a security boundary (the backend checks the FuAdmin role), only a speed bump so nobody
// edits a live prompt by accident.
const PAGE_PASSWORD = 'faceup-ai-prompts'

const passwordSchema = yup.object({
  password: yup.string().required(),
})

export const AiPromptsView = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const { region } = useRegion()

  const passwordForm = useForm({
    schema: passwordSchema,
    afterSubmit: 'persistValues',
    defaultValues: { password: '' },
  })

  return (
    <ContentLayout header={<ContentLayout.Header title='AI prompts' />}>
      <ModalForm
        form={passwordForm}
        open={!isAuthenticated}
        onSubmit={values => {
          if (values.password === PAGE_PASSWORD) {
            setIsAuthenticated(true)
            return true
          }
          passwordForm.setError('password', { message: 'Incorrect password' })
          return false
        }}
        title='Enter password'
        submitButtonText='save'
        isCancelButtonHidden
      >
        <PasswordInput
          control={passwordForm.control}
          name='password'
          label='Password'
          placeholder='Enter password to access this page'
        />
      </ModalForm>

      {isAuthenticated && (
        // Saving in the selected region; the backend copies the prompt to every other region.
        <TrpcProvider region={region ?? DEFAULT_SUPPORTED_REGION}>
          <Suspense fallback={<PageContentSkeleton />}>
            <AiPromptsTable />
          </Suspense>
        </TrpcProvider>
      )}
    </ContentLayout>
  )
}

const AiPromptsTable = () => {
  const { formatDate } = useIntl()
  const { data: prompts } = useSuspenseQuery(trpc.aiPrompt.list.queryOptions())
  const [editing, setEditing] = useState<{ name: AiPromptName; content: string } | null>(null)
  const [historyName, setHistoryName] = useState<AiPromptName | null>(null)

  return (
    <>
      <Table
        shadow
        rowKey='name'
        dataSource={prompts}
        pagination={false}
        columns={[
          {
            title: 'Prompt',
            key: 'prompt',
            render: prompt => (
              <div className='flex flex-col'>
                <Text strong>{aiPromptDefinitions[prompt.name].label}</Text>
                <Text type='secondary'>{aiPromptDefinitions[prompt.name].description}</Text>
              </div>
            ),
          },
          {
            title: 'Last change',
            key: 'lastChange',
            render: ({ current }) =>
              current ? (
                <div className='flex flex-col'>
                  <Text>
                    {formatDate(current.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  </Text>
                  {current.note && <Text type='secondary'>{current.note}</Text>}
                </div>
              ) : (
                <Text type='warning'>Not configured</Text>
              ),
          },
          {
            title: '',
            key: 'actions',
            render: prompt => (
              <div className='flex justify-end gap-2'>
                <Button
                  onClick={() =>
                    setEditing({ name: prompt.name, content: prompt.current?.content ?? '' })
                  }
                >
                  Edit
                </Button>
                <Button onClick={() => setHistoryName(prompt.name)} disabled={!prompt.current}>
                  History
                </Button>
              </div>
            ),
          },
        ]}
      />
      {editing && (
        <EditAiPromptModal
          name={editing.name}
          content={editing.content}
          onClose={() => setEditing(null)}
        />
      )}
      {historyName && (
        <AiPromptHistoryModal name={historyName} onClose={() => setHistoryName(null)} />
      )}
    </>
  )
}
